import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  KeyRound,
  MoreHorizontal,
  Search,
  Send,
  Star,
  Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { queryKeys } from "@/lib/query-keys";
import { useResendPartnerInvite } from "@/features/partners/use-resend-partner-invite";
import { useRevokePartnerInvite } from "@/features/partners/use-revoke-partner-invite";
import { useRemovePartnerUser } from "@/features/partners/use-remove-partner-user";
import { usePartnerContacts } from "../use-partner-contacts";
import { useResetUserPassword } from "../use-team-mutations";
import { UserStatusBadge } from "./status-badge";

export function PartnerContactsTab() {
  const qc = useQueryClient();
  const contactsQuery = usePartnerContacts();
  const [query, setQuery] = useState("");

  const resend = useResendPartnerInvite();
  const revoke = useRevokePartnerInvite();
  const remove = useRemovePartnerUser();
  const resetPw = useResetUserPassword();

  // Reused partner hooks invalidate the partner-detail caches; also refresh the
  // unified directory this tab reads.
  const refresh = () =>
    qc.invalidateQueries({ queryKey: queryKeys.users.partnerContacts() });

  const rows = contactsQuery.data ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.email.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.partnerName.toLowerCase().includes(q),
    );
  }, [rows, query]);

  if (contactsQuery.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email or partner…"
          className="h-9 pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No partner contacts"
          description="Invite partner contacts from a partner's detail page (People tab)."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead className="w-40">Role at partner</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 truncate text-sm font-medium">
                        {c.name}
                        {c.isPrimary ? (
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                        ) : null}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {c.email}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{c.partnerName}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {c.roleAtPartner || "—"}
                  </TableCell>
                  <TableCell>
                    <UserStatusBadge status={c.status} />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {c.userId ? (
                          <DropdownMenuItem
                            onClick={() =>
                              c.userId &&
                              resetPw.mutate({ userId: c.userId })
                            }
                          >
                            <KeyRound className="mr-2 h-4 w-4" />
                            Send password reset
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() =>
                              resend.mutate(
                                { partnerId: c.partnerId, partnerUserId: c.id },
                                { onSuccess: refresh },
                              )
                            }
                          >
                            <Send className="mr-2 h-4 w-4" />
                            Resend invite
                          </DropdownMenuItem>
                        )}
                        {!c.userId && (
                          <DropdownMenuItem
                            onClick={() =>
                              revoke.mutate(
                                { partnerId: c.partnerId, partnerUserId: c.id },
                                { onSuccess: refresh },
                              )
                            }
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Revoke invite
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() =>
                            remove.mutate(
                              { partnerId: c.partnerId, partnerUserId: c.id },
                              { onSuccess: refresh },
                            )
                          }
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove contact
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
